import { useEffect, useState } from 'react';
import {
  BadgeCheck,
  Check,
  ExternalLink,
  FileStack,
  LoaderCircle,
  Minus,
  Plus,
  RefreshCw,
  TriangleAlert,
} from 'lucide-react';

import {
  pagePackListPriceUsd,
  pagePackPages,
  type PageAllowanceView,
} from '../shared/v6-page-allowance';
import './page-allowance.css';

const number = (value: number) => value.toLocaleString('en-US');
const day = (utc: string) =>
  new Date(utc).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

/** The Lite plan's pages as main sees them; null until main answers. Not metered: `metered` false. */
export function usePageAllowance(): PageAllowanceView | null {
  const [view, setView] = useState<PageAllowanceView | null>(null);
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!api?.getPageAllowance) {
      setView({ metered: false, tier: null });
      return;
    }
    let live = true;
    void api
      .getPageAllowance()
      .then((next) => {
        if (live) setView(next);
      })
      .catch(() => {
        if (live) setView({ metered: false, tier: null });
      });
    const stop = api.onPageAllowanceChanged?.((next) => {
      if (live) setView(next);
    });
    // Coming back from the browser (a pack checkout) checks in, so bought pages show at once.
    const onFocus = () => {
      void api
        .checkInPages?.()
        .then((next) => {
          if (live) setView(next);
        })
        .catch(() => undefined);
    };
    window.addEventListener('focus', onFocus);
    return () => {
      live = false;
      stop?.();
      window.removeEventListener('focus', onFocus);
    };
  }, []);
  return view;
}

/** Packs bought now: the list price, and the partner's discount when Phone Home answered one. */
export const packPrice = (packs: number, discountPct: number | null) => {
  const list = packs * pagePackListPriceUsd;
  const due = discountPct ? Math.round(list * (100 - discountPct)) / 100 : list;
  return { list, due };
};

/**
 * Settings, License: the Lite plan's usage meter, the 90% warning, Buy pages and the Enterprise quote
 * link. Custom shows "Unlimited" and nothing else; an unmetered license shows nothing.
 */
export function PageAllowancePanel({
  view: given,
  onCustomQuote,
  onChangePlan,
}: {
  view?: PageAllowanceView | null;
  onCustomQuote?: () => void;
  onChangePlan?: () => void;
}) {
  const live = usePageAllowance();
  const [view, setView] = useState<PageAllowanceView | null>(null);
  useEffect(() => {
    setView(given ?? live);
  }, [given, live]);
  const [buying, setBuying] = useState(false);
  const [packs, setPacks] = useState(1);
  const [busy, setBusy] = useState<'check' | 'buy' | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'problem'; text: string } | null>(null);
  if (view === null || (!view.metered && view.tier === null)) return null;
  if (!view.metered)
    return (
      <section className="page-allowance custom" aria-label="Pages">
        <BadgeCheck size={20} aria-hidden="true" />
        <div>
          <span>Pages</span>
          <strong>Unlimited</strong>
        </div>
      </section>
    );
  const total = view.included.pages + view.packs.reduce((sum, pack) => sum + pack.pages, 0);
  const share = (value: number) =>
    total <= 0 ? 0 : Math.max(0, Math.min(100, (value / total) * 100));
  const includedLeft = Math.max(0, Math.min(view.included.left, view.localBalance));
  const packLeft = Math.max(0, view.localBalance - includedLeft);
  const empty = view.localBalance <= 0;
  const tone = view.checkinOverdue || empty ? 'stopped' : view.lowBalance ? 'low' : 'ok';
  const price = packPrice(packs, view.partnerDiscountPct);
  const checkIn = async () => {
    setBusy('check');
    try {
      const next = await window.capturePointDesktop?.checkInPages?.();
      if (next) setView(next);
    } finally {
      setBusy(null);
    }
  };
  const buy = async () => {
    setBusy('buy');
    setMessage(null);
    try {
      const answer = await window.capturePointDesktop?.buyPages?.(packs);
      if (!answer) setMessage({ tone: 'problem', text: 'Buying pages is not available here.' });
      else if (answer.started) {
        setBuying(false);
        setMessage({
          tone: 'ok',
          text: 'Checkout open in your browser. The pages appear here after payment.',
        });
      } else setMessage({ tone: 'problem', text: answer.message });
    } catch {
      setMessage({ tone: 'problem', text: "Checkout didn't open. Try again in a moment." });
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className={`page-allowance ${tone}`} aria-label="Pages">
      <div className="page-allowance-head">
        <div>
          <span>
            {view.tier === 'lite-yearly' ? 'Lite · yearly' : 'Lite · monthly'} · resets{' '}
            {day(view.periodEndUtc)}
          </span>
          <strong>
            <b>{number(view.localBalance)}</b> pages left
          </strong>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label="Check pages now"
          title="Check pages now"
          disabled={busy !== null}
          onClick={() => void checkIn()}
        >
          <RefreshCw size={17} className={busy === 'check' ? 'spin' : undefined} />
        </button>
      </div>
      <div
        className="page-allowance-meter"
        role="meter"
        aria-label="Pages left"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={view.localBalance}
      >
        <span className="included" style={{ width: `${share(includedLeft)}%` }} />
        <span className="packs" style={{ width: `${share(packLeft)}%` }} />
      </div>
      <ul className="page-allowance-rows">
        <li>
          <span className="swatch included" aria-hidden="true" />
          <span>Included</span>
          <span>
            {number(view.included.left)} of {number(view.included.pages)} left
          </span>
        </li>
        {view.packs.map((pack, index) => (
          <li key={`${pack.expiresUtc}-${index}`}>
            <span className="swatch packs" aria-hidden="true" />
            <span>Pack</span>
            <span>
              {number(pack.left)} of {number(pack.pages)} · until {day(pack.expiresUtc)}
              {pack.expiresSoon && <em>Expires soon</em>}
            </span>
          </li>
        ))}
        {/* Pages left is the snapshot less what this computer counted since: say so, so the rows add up. */}
        {(view.pendingPages > 0 || view.reservedPages > 0) && (
          <li className="page-allowance-local">
            <span className="swatch local" aria-hidden="true" />
            <span>This computer</span>
            <span>
              {[
                view.pendingPages > 0 && `${number(view.pendingPages)} counted, not yet synced`,
                view.reservedPages > 0 && `${number(view.reservedPages)} being read`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </li>
        )}
      </ul>
      {view.checkinOverdue ? (
        <p className="page-allowance-note" role="status">
          <TriangleAlert size={15} aria-hidden="true" />
          Check-in needed. Connect to the Internet; new documents wait until Paige checks in.
        </p>
      ) : empty ? (
        <p className="page-allowance-note" role="status">
          <TriangleAlert size={15} aria-hidden="true" />
          Pages used up. New documents wait until you buy pages or the plan resets on{' '}
          {day(view.periodEndUtc)}.
        </p>
      ) : (
        view.lowBalance && (
          <p className="page-allowance-note" role="status">
            <TriangleAlert size={15} aria-hidden="true" />
            Running low: under 10% of this period's pages are left.
          </p>
        )
      )}
      {message && (
        <p
          className={`page-allowance-message ${message.tone}`}
          role={message.tone === 'problem' ? 'alert' : 'status'}
        >
          {message.tone === 'problem' ? (
            <TriangleAlert size={15} aria-hidden="true" />
          ) : (
            <Check size={15} aria-hidden="true" />
          )}
          {message.text}
        </p>
      )}
      {buying ? (
        <div className="page-allowance-buy" role="group" aria-label="Buy pages">
          <div className="page-allowance-stepper">
            <button
              type="button"
              className="icon-button"
              aria-label="Fewer packs"
              disabled={packs <= 1 || busy !== null}
              onClick={() => setPacks((value) => Math.max(1, value - 1))}
            >
              <Minus size={16} />
            </button>
            <output aria-live="polite">
              <strong>{number(packs * pagePackPages)}</strong>
              <small>
                pages · {packs} {packs === 1 ? 'pack' : 'packs'}
              </small>
            </output>
            <button
              type="button"
              className="icon-button"
              aria-label="More packs"
              disabled={packs >= 100 || busy !== null}
              onClick={() => setPacks((value) => Math.min(100, value + 1))}
            >
              <Plus size={16} />
            </button>
          </div>
          <p className="page-allowance-price">
            {view.partnerDiscountPct ? (
              <>
                <s>${number(price.list)}</s>{' '}
                <strong>
                  $
                  {price.due.toLocaleString('en-US', {
                    minimumFractionDigits: price.due % 1 ? 2 : 0,
                  })}
                </strong>{' '}
                <small>Your partner's discount: {view.partnerDiscountPct}% off</small>
              </>
            ) : (
              <>
                <strong>${number(price.list)}</strong>{' '}
                <small>$40 per 1,000 pages · used after included pages · 12 months</small>
              </>
            )}
          </p>
          <div className="page-allowance-actions">
            <button
              type="button"
              className="primary-button"
              disabled={busy !== null}
              onClick={() => void buy()}
            >
              {busy === 'buy' ? (
                <LoaderCircle size={17} className="spin" />
              ) : (
                <ExternalLink size={17} />
              )}
              Open checkout
            </button>
            <button
              type="button"
              className="quiet-link"
              disabled={busy !== null}
              onClick={() => setBuying(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="page-allowance-actions">
          <button
            type="button"
            className="secondary-button"
            disabled={busy !== null}
            onClick={() => {
              setMessage(null);
              setBuying(true);
            }}
          >
            <FileStack size={17} />
            Buy pages
          </button>
          {onChangePlan && (
            <button
              type="button"
              className="quiet-link"
              data-telemetry="meter-change-plan"
              onClick={onChangePlan}
            >
              Change plan
            </button>
          )}
          {onCustomQuote && (
            <button type="button" className="quiet-link" onClick={onCustomQuote}>
              Need more? Ask for an Enterprise quote
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * The calm status-bar note for a Lite plan that is running low, used up, or due a check-in. It
 * opens Settings, License, where the meter and Buy pages are.
 */
export function PagesTray() {
  const view = usePageAllowance();
  if (!view?.metered) return null;
  const empty = view.localBalance <= 0;
  if (!view.checkinOverdue && !empty && !view.lowBalance) return null;
  const label = view.checkinOverdue
    ? 'Check-in needed'
    : empty
      ? 'Pages used up'
      : `${number(view.localBalance)} pages left`;
  return (
    <button
      type="button"
      className={`pages-tray${view.checkinOverdue || empty ? ' stopped' : ''}`}
      aria-label={`${label}. Open License`}
      title="Open License"
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => window.dispatchEvent(new Event('capturepoint-open-license'))}
    >
      <FileStack size={16} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}
